import PrimaryInnerGlowButton from '@/components/buttons/primary';
import Checkbox from '@/components/forms/Checkbox';
import InputField from '@/components/forms/InputField';
import SelectField from '@/components/forms/SelectField';
import TermsAndPrivacyNotice from '@/components/forms/TermsAndPrivacyNotice';
import Image from '@/components/Image';
import { Container } from '@/components/layout/Container';
import Link from '@/components/Link';
import { PRIMARY_NAV_HEIGHT } from '@/components/navigation/PrimaryNavigation';
import YouTubeVideo from '@/components/YouTubeVideo';
import { GTM_VARIABLES } from '@/constants/gtm';
import {
  HubspotCompanySizeDropdownOptions,
  HubspotFieldLabels,
  HubspotFieldNames,
  HubspotFieldValidators,
  HubspotFormIDs,
  HubspotIndustryDropdownOptions,
} from '@/constants/hubspot';
import { WATCH_DEMO } from '@/constants/routes';
import { WATCH_DEMO_BYPASS_KEY } from '@/constants/session-storage';
import { GlobalContext } from '@/context';
import { OneTrustContext } from '@/context/onetrust';
import { ChiliPiperSession, ChiliPiperTargetID } from '@/lib/chili-piper';
import { useCustomFormik, useFormikHubspotSubmit } from '@/lib/formik';
import gtm from '@/lib/gtm';
import { setBypassCookie } from '@/lib/hooks/useBypassCookie';
import { checkHubspotEmailDomain } from '@/lib/hubspot';
import { maybeNotSet } from '@/lib/typescript-helpers';
import { extractYouTubeId } from '@/lib/youtube';
import { COLORS } from '@/styles/color';
import { ChiliPiperTarget } from '@/styles/GlobalStyles';
import { mediaBreakpointUp, paddingX } from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

// #region - Components

const PageWrapper = styled.section`
  padding-top: ${remCalc(PRIMARY_NAV_HEIGHT + 40)};
  background-color: ${COLORS.GRAY.HIGHLIGHT};

  ${mediaBreakpointUp('desktop')} {
    padding-top: ${remCalc(PRIMARY_NAV_HEIGHT + 120)};
    padding-bottom: ${remCalc(120)};
  }
`;

const Headline = styled.h1`
  ${customTextSize(32, 36)}
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  margin-bottom: ${remCalc(24)};

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(49, 58)}
    margin-bottom: ${remCalc(16)};
  }
`;

const FormTitle = styled.div`
  ${customTextSize(16, 20)};
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  margin-bottom: ${remCalc(20)};
  text-align: center;

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(24, 34)};
  }
`;

const ContentWrapper = styled.div`
  background-color: ${COLORS.WHITE};
  border-radius: ${remCalc(10)};
  padding: ${remCalc(20)};
`;

const SuccessHeadline = styled(Headline)`
  text-align: center;

  ${mediaBreakpointUp('desktop')} {
    margin-bottom: ${remCalc(44)};
  }
`;

const SuccessStepWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  row-gap: ${remCalc(40)};
  padding: ${remCalc(20)};

  ${mediaBreakpointUp('desktop')} {
    flex-direction: row;
    justify-content: center;
    padding: ${remCalc(60, 0)};
  }
`;

const SuccessStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  ${mediaBreakpointUp('desktop')} {
    ${paddingX(34)}
    flex-basis: 1;
    max-width: ${remCalc(264)};
    box-sizing: content-box;
  }
`;

const SuccessStepHeadline = styled.p`
  ${customTextSize(16, 20)}
  font-weight: ${FONT_WEIGHTS.BOLD};
  text-align: center;
  margin-bottom: ${remCalc(16)};

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(24, 24)}
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  }
`;

const SuccessStepText = styled.p`
  ${customTextSize(16, 24)}
  text-align: center;

  a {
    text-decoration: underline;
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  }
`;

const SuccessStepIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  width: ${remCalc(164)};
  height: ${remCalc(164)};
  margin-bottom: ${remCalc(16)};

  background: radial-gradient(50% 50% at 50% 50%, rgba(255, 207, 0, 0.1) 0%, rgba(255, 207, 0, 0) 100%);
`;

const StyledYouTubeVideo = styled(YouTubeVideo)`
  border-radius: ${remCalc(10)};
  overflow: hidden;
  background-color: ${COLORS.WHITE};
`;

const PhoneConfirmationWrapper = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;

  p {
    margin-bottom: 1.5rem;
  }

  ${PrimaryInnerGlowButton} {
    margin: 0 auto;
  }
`;

// #endregion - Components

interface ModernRequestDemoProps {
  pageName: string;
  videoUrl?: string;
  videoCover?: string;
  formSubmitted?: boolean;
}

const ModernRequestDemo: FC<ModernRequestDemoProps> = (props) => {
  const { showGetStartedModal } = useContext(GlobalContext);
  const [formSubmitted, setFormSubmitted] = useState<boolean>(props.formSubmitted ?? false);
  const [showFullScreenConfirmation, setShowFullScreenConfirmation] = useState<boolean>(props.formSubmitted ?? false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [showFindAnotherTimeCTA, setShowFindAnotherTimeCTA] = useState(false);
  const [meetingType, setMeetingType] = useState<'scheduled' | 'phone' | null>(null);
  const chiliPiperSessionRef = useRef<ChiliPiperSession | null>(null);
  const [showQuickThankYou, setshowQuickThankYou] = useState<boolean>(false);

  const { isGDPR } = useContext(OneTrustContext);
  const [marketingEmailOptIn, setMarketingEmailOptIn] = useState(isGDPR ? false : true);

  useEffect(() => {
    setMarketingEmailOptIn(isGDPR ? false : true);
    {
      isGDPR && (
        formik.setFieldValue(HubspotFieldNames.MarketingEmailOptOut, true)
      )
    }
  }, [isGDPR]);

  useEffect(() => {
    if (props.formSubmitted) {
      setFormSubmitted(props.formSubmitted);
      setShowFullScreenConfirmation(props.formSubmitted);
    }
  }, [props.formSubmitted]);

  useEffect(() => {
    return () => {
      if (chiliPiperSessionRef.current) {
        chiliPiperSessionRef.current.destroy();
      }
    };
  }, []);

  const { youtubeVideoId, isVideoDemo, formId } = useMemo(() => {
    const youtubeVideoId = !!props.videoUrl ? extractYouTubeId(props.videoUrl) : null;
    const isVideoDemo = !!youtubeVideoId;
    const formId = isVideoDemo ? HubspotFormIDs.WatchDemo : HubspotFormIDs.RequestDemo;

    return {
      youtubeVideoId,
      isVideoDemo,
      formId,
    };
  }, [props.videoUrl]);

  const CheckMark = () => {
    return (<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
      <g clip-path="url(#clip0_4174_9883)">
        <path d="M16.5 8.31002V9.00002C16.4991 10.6173 15.9754 12.191 15.007 13.4864C14.0386 14.7818 12.6775 15.7294 11.1265 16.1879C9.57557 16.6465 7.91794 16.5914 6.40085 16.031C4.88376 15.4705 3.58849 14.4346 2.70822 13.0778C1.82795 11.721 1.40984 10.1161 1.51626 8.50226C1.62267 6.88844 2.24791 5.35227 3.29871 4.12283C4.34951 2.89338 5.76959 2.03656 7.34714 1.68013C8.92469 1.3237 10.5752 1.48677 12.0525 2.14502" stroke="#0D1D39" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M16.5 3L9 10.5075L6.75 8.2575" stroke="#0D1D39" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      </g>
      <defs>
        <clipPath id="clip0_4174_9883">
          <rect width="18" height="18" fill="white" />
        </clipPath>
      </defs>
    </svg>)
  }
  const formik = useCustomFormik({
    initialValues: {
      [HubspotFieldNames.Email]: '',
      [HubspotFieldNames.FirstName]: '',
      [HubspotFieldNames.LastName]: '',
      [HubspotFieldNames.CompanyName]: '',
      [HubspotFieldNames.JobTitle]: '',
      [HubspotFieldNames.CompanySize]: '',
      [HubspotFieldNames.Industry]: '',
      [HubspotFieldNames.MarketingEmailOptOut]: ''
    },
    onSubmit: useFormikHubspotSubmit({
      formId,
      name: `${props.pageName} Form`,
      content: `${props.pageName} Form`,
      onSuccess: async (values) => {
        setFormSubmitted(true);

        const getCompanySize = values[HubspotFieldNames.CompanySize];

        if (Number(getCompanySize) > 10) {
          chiliPiperSessionRef.current = new ChiliPiperSession(
            {
              email: values[HubspotFieldNames.Email],
              first_name: values[HubspotFieldNames.FirstName],
              last_name: values[HubspotFieldNames.LastName],
              company_name: values[HubspotFieldNames.CompanyName],
              company_size: values[HubspotFieldNames.CompanySize],
              industry: values[HubspotFieldNames.Industry],
              phone: '',
            },
            (eventName, eventArgs) => {
              switch (eventName) {
                case 'phone-selected':
                  setShowFindAnotherTimeCTA(false);
                  setMeetingType('phone');
                  break;
                case 'availability-loaded':
                  setMeetingType('scheduled');
                  setShowFindAnotherTimeCTA(true);
                  break;
                case 'timeslot-selected':
                  setShowFindAnotherTimeCTA(false);
                  break;
                case 'booked':
                case 'booking-confirmed':
                  setShowConfirmation(true);
                  break;
                case 'phoneClose':
                  setShowConfirmation(true);
                  break;
                case 'onClose':
                case 'onError':
                  setShowFullScreenConfirmation(true);
                  break;
              }
            },
          );

        } else {
          setShowFullScreenConfirmation(false);
          setshowQuickThankYou(true);
        }

        if (formId === HubspotFormIDs.RequestDemo) {
          const { isPayingCustomer } = await checkHubspotEmailDomain(values[HubspotFieldNames.Email]);

          gtm.trackDemoRequest({
            [GTM_VARIABLES.USER_EMAIL]: values[HubspotFieldNames.Email],
            [GTM_VARIABLES.USER_FIRST_NAME]: values[HubspotFieldNames.FirstName],
            [GTM_VARIABLES.USER_LAST_NAME]: values[HubspotFieldNames.LastName],
          });

          if (isPayingCustomer) {
            setShowFullScreenConfirmation(true);

            if (chiliPiperSessionRef.current) {
              chiliPiperSessionRef.current.markExistingCustomer();
            }
          } else {
            if (chiliPiperSessionRef.current) {
              chiliPiperSessionRef.current.loadFrame();
            }
          }
        } else {
          setShowFullScreenConfirmation(true);
        }
      },
    }),
    validate: (values) => {
      const errors = {};

      errors[HubspotFieldNames.Email] = HubspotFieldValidators[HubspotFieldNames.Email](
        values[HubspotFieldNames.Email],
        {
          required: true,
          allowPersonalEmail: true,
          allowSharedEmail: true,
        },
      );

      errors[HubspotFieldNames.FirstName] = HubspotFieldValidators[HubspotFieldNames.FirstName](
        values[HubspotFieldNames.FirstName],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.LastName] = HubspotFieldValidators[HubspotFieldNames.LastName](
        values[HubspotFieldNames.LastName],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.CompanyName] = HubspotFieldValidators[HubspotFieldNames.CompanyName](
        values[HubspotFieldNames.CompanyName],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.JobTitle] = HubspotFieldValidators[HubspotFieldNames.JobTitle](
        values[HubspotFieldNames.JobTitle],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.CompanySize] = HubspotFieldValidators[HubspotFieldNames.CompanySize](
        values[HubspotFieldNames.CompanySize],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.Industry] = HubspotFieldValidators[HubspotFieldNames.Industry](
        values[HubspotFieldNames.Industry],
        {
          required: true,
        },
      );

      if (Object.values(errors).every((val) => val === undefined)) return;

      return errors;
    },
  });

  const onDemoClick = () => {
    setBypassCookie(WATCH_DEMO_BYPASS_KEY);

    gtm.track('click', {
      element_type: 'link',
      element_label: 'Product Demo',
      location: `Form Success - ${props.pageName}`,
    });
  };

  if (showFullScreenConfirmation) {
    if (isVideoDemo) {
      return (
        <PageWrapper>
          <Container>
            <SuccessHeadline>Here is your video demo</SuccessHeadline>

            <ContentWrapper>
              <StyledYouTubeVideo
                coverSrc={props.videoCover}
                videoId={youtubeVideoId!}
                onInitialPlay={() => {
                  gtm.track('play_video', {
                    location: `Form Success - ${props.pageName}`,
                    video_youtube_id: youtubeVideoId!,
                    video_url: maybeNotSet(props.videoUrl),
                  });
                }}
              />
            </ContentWrapper>


          </Container>
        </PageWrapper>
      );
    }

    return (
      <PageWrapper>
        <Container>
          <SuccessHeadline>Thank you! What you can expect next.</SuccessHeadline>

          <ContentWrapper>
            <SuccessStepWrapper>
              <SuccessStep>
                <SuccessStepHeadline>1. We’ll be in touch</SuccessStepHeadline>
                <SuccessStepIcon>
                  <Image src="/webassets/pages/get-started/check-inbox.png" width={228} height={182} alt="" />
                </SuccessStepIcon>
                <SuccessStepText>
                  A product expert will call or email you soon to set up a time to chat.
                </SuccessStepText>
              </SuccessStep>

              <SuccessStep>
                <SuccessStepHeadline>2. Mark your calendar!</SuccessStepHeadline>
                <SuccessStepIcon>
                  <Image src="/webassets/pages/get-started/select-time.png" width={280} height={258} alt="" />
                </SuccessStepIcon>
                <SuccessStepText>
                  We’ll ask about your workflows to prepare a personalized demo that fits your team’s unique needs.
                </SuccessStepText>
              </SuccessStep>

              <SuccessStep>
                <SuccessStepHeadline>3. In the meantime</SuccessStepHeadline>
                <SuccessStepIcon>
                  <Image src="/webassets/pages/get-started/watch-video.png" width={264} height={216} alt="" />
                </SuccessStepIcon>
                <SuccessStepText>
                  Want to see Front while you’re waiting? Check out our{' '}
                  <Link href={WATCH_DEMO}>
                    <a onClick={onDemoClick}>video demo</a>
                  </Link>
                  .
                </SuccessStepText>
              </SuccessStep>
            </SuccessStepWrapper>
          </ContentWrapper>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <>
      {!showQuickThankYou && (<div className="px-container-mobile md:px-container-desktop grid grid-cols-4 md:grid-cols-12 gap-y-6 mt-[100px] py-[100px]">
        <div className="col-start-1 col-end-12 md:col-end-6">
          <div className="flex w-full">
            <div className="flex flex-col w-full">
              {formSubmitted ? null : (
                <>
                  <div className="flex text-h3 text-blue-1000 font-500">Get a personalized demo</div>
                  <div className="flex text-caption1 text-blue-1000 font-500 mb-[24px]">Help us tailor the demo experience to your needs.</div>

                  <form onSubmit={formik.handleSubmit}>
                    <InputField
                      type="email"
                      name={HubspotFieldNames.Email}
                      label={HubspotFieldLabels.Email}
                      placeholder="you@company.com"
                      value={formik.values[HubspotFieldNames.Email]}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched[HubspotFieldNames.Email] && !!formik.errors[HubspotFieldNames.Email]}
                      errorMessage={formik.errors[HubspotFieldNames.Email] as string}
                      className="mb-[24px]"
                    />

                    <div className="flex w-full mb-[24px]">
                      <InputField
                        type="text"
                        name={HubspotFieldNames.FirstName}
                        label={HubspotFieldLabels.FirstName}
                        placeholder="Jamie"
                        value={formik.values[HubspotFieldNames.FirstName]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched[HubspotFieldNames.FirstName] && !!formik.errors[HubspotFieldNames.FirstName]
                        }
                        errorMessage={formik.errors[HubspotFieldNames.FirstName] as string}
                        className="w-1/2 mr-[8px]"
                      />

                      <InputField
                        type="text"
                        name={HubspotFieldNames.LastName}
                        label={HubspotFieldLabels.LastName}
                        placeholder="Smith"
                        value={formik.values[HubspotFieldNames.LastName]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched[HubspotFieldNames.LastName] && !!formik.errors[HubspotFieldNames.LastName]
                        }
                        errorMessage={formik.errors[HubspotFieldNames.LastName] as string}
                        className="w-1/2"
                      />
                    </div>

                    <div className="flex w-full mb-[24px]">
                      <InputField
                        type="text"
                        name={HubspotFieldNames.CompanyName}
                        label={HubspotFieldLabels.CompanyName}
                        placeholder="Your Company"
                        value={formik.values[HubspotFieldNames.CompanyName]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched[HubspotFieldNames.CompanyName] &&
                          !!formik.errors[HubspotFieldNames.CompanyName]
                        }
                        errorMessage={formik.errors[HubspotFieldNames.CompanyName] as string}
                        className="w-1/2 mr-[8px]"
                      />

                      <InputField
                        type="text"
                        name={HubspotFieldNames.JobTitle}
                        label={HubspotFieldLabels.JobTitle}
                        placeholder="e.g., Director of Operations"
                        value={formik.values[HubspotFieldNames.JobTitle]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched[HubspotFieldNames.JobTitle] && !!formik.errors[HubspotFieldNames.JobTitle]
                        }
                        errorMessage={formik.errors[HubspotFieldNames.JobTitle] as string}
                        className="w-1/2 mr-[8px]"
                      />
                    </div>

                    <div className="flex w-full mb-[24px]">
                      <SelectField
                        name={HubspotFieldNames.CompanySize}
                        label={HubspotFieldLabels.CompanySize}
                        options={HubspotCompanySizeDropdownOptions}
                        value={formik.values[HubspotFieldNames.CompanySize]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched[HubspotFieldNames.CompanySize] &&
                          !!formik.errors[HubspotFieldNames.CompanySize]
                        }
                        errorMessage={formik.errors[HubspotFieldNames.CompanySize] as string}
                        className="w-1/2 mr-[8px]"
                      />

                      <SelectField
                        name={HubspotFieldNames.Industry}
                        label={HubspotFieldLabels.Industry}
                        options={HubspotIndustryDropdownOptions}
                        value={formik.values[HubspotFieldNames.Industry]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched[HubspotFieldNames.Industry] && !!formik.errors[HubspotFieldNames.Industry]
                        }
                        errorMessage={formik.errors[HubspotFieldNames.Industry] as string}
                        translucent
                        className="w-1/2 mr-[8px]"
                      />
                    </div>

                    {isGDPR && (
                      <div className="flex my-[24px]">
                        <Checkbox
                          id={HubspotFieldNames.MarketingEmailOptOut}
                          checked={marketingEmailOptIn}
                          onToggle={(checked) => {
                            // Since we're using the same field for both the signup and demo modal, we need to invert the value since we're opting IN rather than opting out
                            setMarketingEmailOptIn(checked);
                            formik.setFieldValue(HubspotFieldNames.MarketingEmailOptOut, !checked);
                          }}
                        />
                        <label htmlFor={HubspotFieldNames.MarketingEmailOptOut} className="ml-[8px]">
                          Opt into emails with the latest news, offers, promotions, and resources from Front.
                        </label>
                      </div>
                    )}

                    <TermsAndPrivacyNotice />

                    <PrimaryInnerGlowButton type="submit" disabled={formik.isValidating || formik.isSubmitting}>
                      {formik.isValidating || formik.isSubmitting ? 'Submitting' : 'Submit'}
                    </PrimaryInnerGlowButton>
                  </form>
                </>
              )}

              {showConfirmation && meetingType === 'phone' ? (
                <PhoneConfirmationWrapper>
                  <FormTitle>Thanks!</FormTitle>

                  <p>
                    Hopefully we were able to connect. If not, please schedule some dedicated time with our sales team.
                  </p>

                  <PrimaryInnerGlowButton
                    onClick={() => {
                      setShowFullScreenConfirmation(false);
                      setShowConfirmation(false);
                      setMeetingType(null);
                      setShowFindAnotherTimeCTA(false);

                      if (chiliPiperSessionRef.current) {
                        chiliPiperSessionRef.current.destroy();
                        chiliPiperSessionRef.current.loadFrame(true);
                      }
                    }}
                  >
                    Book a meeting
                  </PrimaryInnerGlowButton>
                </PhoneConfirmationWrapper>
              ) : null}

              {!isVideoDemo && !showGetStartedModal ? (
                <ChiliPiperTarget
                  id={ChiliPiperTargetID}
                  key="chilipiper"
                  style={{
                    display: showConfirmation && meetingType == 'phone' ? 'none' : 'block',
                  }}
                />
              ) : null}
            </div>
          </div>
        </div>
        <div className="col-start-7 col-span-6 bg-violet-300 relative rounded-[16px]">
          <div className="ml-[50px]">
            <div className="flex w-full mt-[24px] mb-[24px]">
              <h4 className="text-p2 text-blue-1000 pr-[24px]">Discover how to boost agent efficiency and deliver seamless service with Front, the only platform with:</h4>
            </div>
            <div className="flex w-full flex-wrap">
              <div className="flex w-full text-caption1 font-500">
                <span className="mr-[8px] mb-[12px]">{CheckMark()}</span> A unified workspace for every customer interaction
              </div>
              <div className="flex w-full text-caption1 font-500">
                <span className="mr-[8px] mb-[12px]">{CheckMark()}</span> AI-powered workflows for unmatched efficiency
              </div>
              <div className="flex w-full text-caption1 font-500">
                <span className="mr-[8px] mb-[12px]">{CheckMark()}</span> Fast, easy deployment and rapid ROI
              </div>
              <div className="flex w-full text-caption1 font-500">
                <span className="mr-[8px]">{CheckMark()}</span> Award-winning support with 97%+ CSAT
              </div>
            </div>
          </div>
          <div className="mt-4">
            <Image src="https://front.com/webassets/rebrand/request-demo-image-v2.png" width={1105} height={763} alt="Request demo" className="rounded-br-[16px] rounded-bl-[16px]" />
          </div>
        </div>

        {showConfirmation && meetingType === 'scheduled' ? (
          <div>
            <p>In the meantime, explore Front with our video demo.</p>

            <Link href={WATCH_DEMO}>
              <PrimaryInnerGlowButton as="a" onClick={onDemoClick}>
                View Demo
              </PrimaryInnerGlowButton>
            </Link>
          </div>
        ) : null}

        {showFindAnotherTimeCTA ? (
          <div>
            <hr />

            <p>
              <strong>Don&apos;t see a time that works for you?</strong>
              <br />
              Have a Front specialist reach out to find a time that fits your schedule
            </p>

            <PrimaryInnerGlowButton
              onClick={() => {
                setShowFullScreenConfirmation(true);

                if (chiliPiperSessionRef.current) {
                  chiliPiperSessionRef.current.requestFollowUp();
                }
              }}
            >
              Request a different time
            </PrimaryInnerGlowButton>
          </div>
        ) : null}
      </div>
      )

      }
      {showQuickThankYou && setShowFullScreenConfirmation(true)}
    </>
  );
};

ModernRequestDemo.defaultProps = {
  formSubmitted: false,
};

export default ModernRequestDemo;
